'use client';

import { useTranslations } from 'next-intl';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import gsap from 'gsap';
import { useMasterTimeline } from 'hooks/animation/useMasterTimeline';
import { useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { CustomImage } from '..';
import styles from './BgBlurredText.module.css';

export const Content1 = ({ className }: { className?: string }) => {
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);
  const t = useTranslations('ring');

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        'h3 > span',
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.05, // Increased the stagger value for more delay
        },
        0.2,
      );
      tl.set('h3 > span', { autoAlpha: 1 });

      add(tl, 'buy-bg-blurred-content1');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animationInit();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    intersectionObserver.current.observe(container.current as Element);

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={container}
      className={className ? `${styles.content1} ${className}` : styles.content1}
    >
      <h3>
        {
          // 'The ultimate form factor for Sleep and Recovery tracking'
          t('ring.bgBlurredSection.contentA')
            .split(' ')
            .map((text, index) => {
              return <span key={`detail-text-${index}`}>{text} </span>;
            })
        }
      </h3>
    </div>
  );
};

export const Content2 = ({ className }: { className?: string }) => {
  const t = useTranslations('ring');
  const gsapContext = useGsapContext();
  const { add } = useMasterTimeline();
  const container = useRef<HTMLDivElement>(null);
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  // br is desktop-only via CSS (was a JS width < 700 gate)
  const configs = [
    {
      title: (
        <>
          {t('ring.bgBlurredSection.contentB.a')}{' '}
          <br className={styles.desktopBr} />
          {t('ring.bgBlurredSection.contentB.aa')}
        </>
      ),
      // description:
      //   'Fingers provide more accurate biomarker data than wrists due to higher blood flow and arteriole density.',
      description: t('ring.bgBlurredSection.contentB.a_desc'),
    },
    {
      title: (
        <>
          {t('ring.bgBlurredSection.contentB.b')}{' '}
          <br className={styles.desktopBr} />
          {t('ring.bgBlurredSection.contentB.bb')}
        </>
      ),
      description: t('ring.bgBlurredSection.contentB.b_desc'),
    },
    {
      title: (
        <>
          {t('ring.bgBlurredSection.contentB.c')}{' '}
          <br className={styles.desktopBr} />
          {t('ring.bgBlurredSection.contentB.cc')}
        </>
      ),
      description: t('ring.bgBlurredSection.contentB.c_desc'),
    },
  ];

  const animationInit = () => {
    if (!container.current || !window) return;
    gsapContext.add(() => {
      const tl = gsap.timeline({
        defaults: {
          duration: 0.5,
          ease: 'power2.easeInOut',
        },
        scrollTrigger: {
          trigger: container.current,
          start: 'top center ',
          end: '50px',
          scrub: 0.2,
          immediateRender: false,
        },
      });
      tl.fromTo(
        '.config-contents',
        {
          autoAlpha: 0,
        },
        {
          autoAlpha: 1,
          duration: 0.35,
          stagger: 0.06, // Increased the stagger value for more delay
        },
        0.2,
      );
      // t103.set('.config-contents', { autoAlpha: 1 });

      add(tl, 'buy-bg-blurred-content2');
    });
  };

  useIsomorphicLayoutEffect(() => {
    intersectionObserver.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          animationInit();
        }
      },
      {
        root: null,
        threshold: 0,
      },
    );

    intersectionObserver.current.observe(container.current as Element);

    return () => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={container}
      className={className ? `${styles.content2} ${className}` : styles.content2}
    >
      <div className={`config-contents ${styles.configContents}`}>
        {configs.map((config, index) => {
          return (
            <div key={`config-${index}`} className={styles.config}>
              <h3>{config.title}</h3>
              <p>{config.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const BgBlurredText = ({
  className,
  htmlId,
}: {
  className?: string;
  htmlId?: string;
}) => {
  return (
    <div
      className={className ? `${styles.root} ${className}` : styles.root}
      id={htmlId}
    >
      <CustomImage
        src="web_v2/backgrounds/ultrahumanSleepRecovert.png/w_1920"
        alt=""
        fill
        className={styles.imgContainer}
      />
      <div className={styles.backdrop}></div>

      <div className={`${styles.content} ${styles.contentOne}`}>
        <Content1 />
      </div>
      <div className={`${styles.content} ${styles.contentTwo}`}>
        <Content2 />
      </div>
    </div>
  );
};
